/**
 * Acesso restrito (RN-012 / RN-012a) — lista de janelas de bloqueio.
 *
 * É lista, e não formulário, porque a entidade tem VÁRIAS janelas ao longo do tempo: a
 * pré-abertura, a manutenção de sábado, o cutover. As passadas ficam como histórico.
 *
 * O bloqueio acontece no LOGIN: quem não está liberado não recebe token. Por isso não existe
 * tela de aviso dentro do sistema — a mensagem aparece na própria tela de entrada.
 *
 * ATENÇÃO às datas: são INSTANTES em UTC, comparados com o relógio do servidor. Não seguem a
 * convenção de hora literal sem fuso do resto do sistema (utils/datetime.ts) — aqui a conversão
 * é obrigatória, senão a abertura acontece com horas de diferença.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Edit, InfoOutlined, LockOpen, LogoutOutlined, RestoreFromTrash } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import StandardPagination, {
  ITENS_POR_PAGINA_PADRAO,
} from "../components/Pagination/StandardPagination";
import SavingOverlay from "../components/SavingOverlay";
import ConfirmModal from "../components/modals/ConfirmModal";
import AccessRestrictionModal, { formatMoment } from "../components/modals/AccessRestrictionModal";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useAuth } from "../contexts/AuthContext";
import { useRouter } from "../contexts/RouterContext";
import { colors } from "../theme/designSystem";
import { filterFieldSx, searchFieldSx } from "../theme/filterStyles";
import {
  accessRestrictionService,
  type AccessRestrictionCandidate,
  type AccessRestrictionItem,
} from "../services/accessRestrictionService";

const SEARCH_MAX = 100;

const sectionStyle = { mb: 3 };

const rowActionSx = (background: string, hover: string) => ({
  backgroundColor: background,
  "&:hover": { backgroundColor: hover },
});

const normalize = (v: string) => v.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const STATUS_LABEL: Record<string, { texto: string; cor: string }> = {
  vigente: { texto: "Vigente", cor: "#dc3545" },
  agendada: { texto: "Agendada", cor: colors.primary },
  encerrada: { texto: "Encerrada", cor: "#adb5bd" },
  inativa: { texto: "Encerrada antes", cor: "#adb5bd" },
};

type StatusFilter = "todos" | "vigente" | "agendada" | "encerrada";

const EntityAccessRestriction: React.FC = () => {
  const { user } = useAuth();
  const { navigateTo } = useRouter();
  const { toast, showToast, hideToast } = useToast();

  const entityId = user?.entityId ?? "";
  const currentUserId = user?.userId ?? "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState<AccessRestrictionItem[]>([]);
  const [candidates, setCandidates] = useState<AccessRestrictionCandidate[]>([]);
  const [sessionsRevokedAt, setSessionsRevokedAt] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("todos");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITENS_POR_PAGINA_PADRAO);

  const [modalAberto, setModalAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<AccessRestrictionItem | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [aEncerrar, setAEncerrar] = useState<AccessRestrictionItem | null>(null);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const page = await accessRestrictionService.list(entityId);
      setItems(page.items);
      setCandidates(page.candidates);
      setSessionsRevokedAt(page.sessionsRevokedAt);
    } catch {
      showToast("Não foi possível carregar as janelas de bloqueio.", "error");
    } finally {
      setLoading(false);
    }
  }, [entityId, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const vigente = useMemo(() => items.find((i) => i.status === "vigente") ?? null, [items]);

  const filtrados = useMemo(() => {
    const termo = normalize(search.trim());
    return items.filter((i) => {
      if (status === "encerrada" && i.status !== "encerrada" && i.status !== "inativa") return false;
      if (status !== "todos" && status !== "encerrada" && i.status !== status) return false;
      if (termo && !normalize(i.message ?? "").includes(termo)) return false;
      return true;
    });
  }, [items, search, status]);

  const hasFilterChanges = search !== "" || status !== "todos";

  const clearFilters = () => {
    setSearch("");
    setStatus("todos");
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(filtrados.length / itemsPerPage));
  const paged = useMemo(
    () => filtrados.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [filtrados, currentPage, itemsPerPage],
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const abrirNova = () => {
    setEmEdicao(null);
    setModalAberto(true);
  };

  const abrirEdicao = (janela: AccessRestrictionItem) => {
    setEmEdicao(janela);
    setModalAberto(true);
  };

  const mensagemDoErro = (e: unknown, padrao: string) => {
    const erro = e as { response?: { data?: { description?: string } } };
    return erro?.response?.data?.description ?? padrao;
  };

  const salvar = async (payload: {
    startsAt: string;
    endsAt: string;
    message: string;
    allowedUserIds: string[];
  }) => {
    setSaving(true);
    try {
      if (emEdicao) {
        await accessRestrictionService.update(entityId, emEdicao.id, payload);
      } else {
        await accessRestrictionService.create(entityId, payload);
      }
      setModalAberto(false);
      showToast("Janela salva.", "success");
      await load();
    } catch (e: unknown) {
      showToast(mensagemDoErro(e, "Não foi possível salvar a janela."), "error");
    } finally {
      setSaving(false);
    }
  };

  const alternarAtiva = async (janela: AccessRestrictionItem, ativa: boolean) => {
    setAEncerrar(null);
    setSaving(true);
    try {
      await accessRestrictionService.setActive(entityId, janela.id, ativa);
      showToast(ativa ? "Janela reativada." : "Janela encerrada — o sistema está aberto.", "success");
      await load();
    } catch (e: unknown) {
      showToast(mensagemDoErro(e, "Não foi possível alterar a janela."), "error");
    } finally {
      setSaving(false);
    }
  };

  const encerrarSessoes = async () => {
    setConfirmRevoke(false);
    setSaving(true);
    try {
      const novoToken = await accessRestrictionService.revokeSessions(entityId);
      // O token novo foi emitido depois do corte: quem clicou continua dentro, todo mundo mais cai.
      localStorage.setItem("clinic4us-token", novoToken);
      showToast("Sessões encerradas. Os demais usuários precisarão entrar de novo; a sua continua.", "success");
      await load();
    } catch {
      showToast("Não foi possível encerrar as sessões.", "error");
    } finally {
      setSaving(false);
    }
  };

  const periodo = (i: AccessRestrictionItem) => `${formatMoment(i.startsAt)} até ${formatMoment(i.endsAt)}`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                Acesso restrito
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Períodos em que só os usuários liberados conseguem entrar no sistema.
              </Typography>
            </Box>
            <FaqButton />
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Busca pela mensagem"
                  placeholder="Buscar janela"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  fullWidth
                  sx={searchFieldSx}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: SEARCH_MAX }}
                />
                <TextField
                  select
                  size="small"
                  label="Situação"
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value as StatusFilter);
                    setCurrentPage(1);
                  }}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="todos">Todas</MenuItem>
                  <MenuItem value="vigente">Vigente</MenuItem>
                  <MenuItem value="agendada">Agendada</MenuItem>
                  <MenuItem value="encerrada">Encerrada</MenuItem>
                </TextField>
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges || loading} />
                <AddButton onClick={abrirNova} title="Nova janela de bloqueio" />
              </div>
            </div>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
                <Box className={vigente ? "c4u-info-box c4u-info-box--warning" : "c4u-info-box"}>
                  <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
                  <Typography>
                    {vigente
                      ? `O sistema está FECHADO até ${formatMoment(vigente.endsAt)} — ${vigente.allowedCount} usuário(s) conseguem entrar. Criar a senha pelo convite continua funcionando: essa página é pública e não passa pelo login.`
                      : "O sistema está aberto: qualquer usuário com vínculo ativo consegue entrar. Uma janela fecha a entrada até a data marcada, sem impedir a criação de senha pelo convite."}
                  </Typography>
                </Box>

                <Box sx={sectionStyle}>
                  <ResultCount shown={filtrados.length} total={items.length} />

                  {filtrados.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: colors.textSecondary, py: 2 }}>
                      {items.length === 0
                        ? "Nenhuma janela cadastrada — o sistema está aberto. Use o botão acima para criar a primeira."
                        : "Nenhuma janela encontrada com os filtros aplicados."}
                    </Typography>
                  ) : (
                    <Box className="admin-plans-table">
                      <Box className="c4u-table__header">
                        <Box className="admin-plans-header-cell" sx={{ flex: "2 1 300px", minWidth: 250 }}>
                          Período
                        </Box>
                        <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "1 1 260px", minWidth: 200 }}>
                          Mensagem no login
                        </Box>
                        <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 110px" }}>
                          Liberados
                        </Box>
                        <Box className="admin-plans-header-cell" sx={{ flex: "0 0 130px" }}>
                          Situação
                        </Box>
                        <Box
                          className="admin-plans-header-cell no-print"
                          sx={{ flex: "0 0 120px", justifyContent: "flex-end" }}
                        >
                          Ações
                        </Box>
                      </Box>

                      <Box className="admin-plans-table-body">
                        {paged.map((janela) => {
                          const rotulo = STATUS_LABEL[janela.status] ?? STATUS_LABEL.encerrada;
                          const emCurso = janela.status === "vigente" || janela.status === "agendada";
                          return (
                            <Box
                              key={janela.id}
                              className="c4u-table__row"
                            >
                              <Box
                                className="admin-plans-cell admin-plans-name"
                                sx={{ flex: "2 1 300px", minWidth: 250 }}
                                data-label="Período"
                              >
                                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                                  {periodo(janela)}
                                </Typography>
                              </Box>
                              <Box
                                className="admin-plans-cell c4u-col--xl"
                                sx={{ flex: "1 1 260px", minWidth: 200 }}
                                data-label="Mensagem no login"
                              >
                                <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
                                  {janela.message}
                                </Typography>
                              </Box>
                              <Box
                                className="admin-plans-cell c4u-col--md"
                                sx={{ flex: "0 0 110px" }}
                                data-label="Liberados"
                              >
                                <Typography sx={{ fontSize: 13 }}>{janela.allowedCount}</Typography>
                              </Box>
                              <Box
                                className="admin-plans-cell"
                                sx={{ flex: "0 0 130px" }}
                                data-label="Situação"
                              >
                                <Chip
                                  label={rotulo.texto}
                                  size="small"
                                  sx={{ color: "white", fontWeight: 600, backgroundColor: rotulo.cor }}
                                />
                              </Box>
                              <Box
                                className="admin-plans-cell admin-plans-actions no-print"
                                sx={{ flex: "0 0 120px" }}
                                data-label="Ações"
                              >
                                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", width: "100%" }}>
                                  <Tooltip title="Editar janela">
                                    <IconButton
                                      className="c4u-row-action"
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        abrirEdicao(janela);
                                      }}
                                      sx={rowActionSx(colors.primary, colors.primaryHover)}
                                    >
                                      <Edit sx={{ fontSize: "1rem" }} />
                                    </IconButton>
                                  </Tooltip>
                                  {emCurso && (
                                    <Tooltip title="Encerrar agora e abrir o sistema">
                                      <IconButton
                                        className="c4u-row-action"
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setAEncerrar(janela);
                                        }}
                                        sx={rowActionSx("#dc3545", "#c82333")}
                                      >
                                        <LockOpen sx={{ fontSize: "1rem" }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {janela.status === "inativa" && (
                                    <Tooltip title="Reativar janela">
                                      <IconButton
                                        className="c4u-row-action"
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          alternarAtiva(janela, true);
                                        }}
                                        sx={rowActionSx(colors.success, "#218838")}
                                      >
                                        <RestoreFromTrash sx={{ fontSize: "1rem" }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {filtrados.length > 0 && (
                    <StandardPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      itemsPerPage={itemsPerPage}
                      totalItems={filtrados.length}
                      onPageChange={setCurrentPage}
                      onItemsPerPageChange={(n) => {
                        setItemsPerPage(n);
                        setCurrentPage(1);
                      }}
                    />
                  )}
                </Box>

                {/* Ação rara e de impacto: mora numa caixa explicativa, com a explicação ao lado,
                    e não solta no cabeçalho (pedido do Hilton, 03/09). */}
                <Box className="c4u-info-box c4u-info-box--warning">
                  <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ mb: 1 }}>
                      <strong>Encerrar todas as sessões</strong> desconecta os outros usuários que estão com o
                      sistema aberto — a sua sessão continua. Quem tem senha entra de novo em seguida; é a
                      janela vigente que decide quem consegue. Use quando precisar tirar todo mundo de dentro
                      antes de uma manutenção.
                      {sessionsRevokedAt ? ` Último encerramento: ${formatMoment(sessionsRevokedAt)}.` : ""}
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<LogoutOutlined />}
                      onClick={() => setConfirmRevoke(true)}
                      sx={{ textTransform: "none" }}
                    >
                      Encerrar todas as sessões
                    </Button>
                  </Box>
                </Box>
              </>
            )}
          </div>
        </Container>
      </Box>

      <AccessRestrictionModal
        open={modalAberto}
        janela={emEdicao}
        candidates={candidates}
        currentUserId={currentUserId}
        saving={saving}
        onClose={() => setModalAberto(false)}
        onSave={salvar}
      />

      <ConfirmModal
        isOpen={confirmRevoke}
        title="Encerrar todas as sessões"
        message="Todos os outros usuários com sessão aberta serão desconectados na próxima ação que fizerem. A sua sessão continua."
        warningMessage="Quem tem a senha entra de novo em seguida — é a janela vigente que decide quem consegue."
        confirmButtonText="Encerrar sessões"
        type="danger"
        isProcessing={saving}
        onConfirm={encerrarSessoes}
        onCancel={() => setConfirmRevoke(false)}
      />

      <ConfirmModal
        isOpen={aEncerrar !== null}
        title="Encerrar a janela agora"
        message="O sistema volta a aceitar todos os usuários imediatamente, sem esperar a data de liberação."
        warningMessage="A janela fica no histórico e pode ser reativada depois."
        confirmButtonText="Encerrar janela"
        type="warning"
        isProcessing={saving}
        onConfirm={() => aEncerrar && alternarAtiva(aEncerrar, false)}
        onCancel={() => setAEncerrar(null)}
      />

      <SavingOverlay open={saving} />
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default EntityAccessRestriction;
